import React, { useState, useEffect, useRef } from "react";
import { classNames } from "primereact/utils";
import styles from "./AppointmentWizard.module.scss";
import { PreReservationFormDialog } from "../../../common/preReservationForms/PreReservationFormDialog";
import { usePreReservationFormForService } from "../../../common/preReservationForms/usePreReservationFormForReservation";
import {
  buildClientFormContext,
  formatFormPrice,
} from "../../../common/preReservationForms/clientFormContext";
import { IPreReservationFormSubmission } from "../../../common/preReservationForms/models";
import { getErrorToast } from "../../../common/infrastructure/utilities/toastUtility";
import {
  useAppointmentWizard,
  useWizardValidation,
  useWizardAnimations,
  usePrefetchMyReservations,
} from "./hooks";
import { WizardHeader } from "./components/WizardHeader/WizardHeader";
import { ProgressBar } from "./components/ProgressBar/ProgressBar";
import { WizardStep } from "./components/WizardStep/WizardStep";
import { WizardNavigation } from "./components/WizardNavigation/WizardNavigation";
import { StartReservationStep } from "./components/StartReservationStep/StartReservationStep";
import { ServicesSelectionStep } from "./components/ServicesSelectionStep/ServicesSelectionStep";
import { EmployeesSelectionStep } from "./components/EmployeesSelectionStep/EmployeesSelectionStep";
import { DateTimeSelectionStep } from "./components/DateTimeSelectionStep/DateTimeSelectionStep";
import { BookingConfirmationStep } from "./components/BookingConfirmationStep/BookingConfirmationStep";
import { BookingSuccessStep } from "./components/BookingSuccessStep/BookingSuccessStep";
import { LoginStep } from "./components/LoginStep/LoginStep";
import { WizardLoadingScreen } from "./components/WizardLoadingScreen/WizardLoadingScreen";
import { useAppContext } from "../../../AppContext";
import { applyStudioWizardAccent } from "../../../common/infrastructure/utilities/colorUtility";
import { routePaths, callPhoneNumber } from "../../../common/infrastructure/routes/routePaths";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGoogleAuthReturn } from "../../../common/infrastructure/hooks/useGoogleAuthReturn";
import { showCustomConfirmDialog } from "../../../common/components/dialogs/AppConfirmDialog";
import { MusicSuggestionsDialog } from "../musicSuggestions/MusicSuggestionsDialog";
import { DraggableBackButton } from "../../../common/components/layout/components/DraggableBackButton";
import { useLastWhileVisible } from "../../../common/components/AppDialog/hooks/useLastWhileVisible";
import { useStaysMountedToClose } from "../../../common/components/AppDialog/hooks/useStaysMountedToClose";

interface IAppointmentWizardProps {
  className?: string;
}

export const AppointmentWizard = ({ className }: IAppointmentWizardProps) => {
  const { selectedStudio, currentUser } = useAppContext();
  const [hasAttemptedProceed, setHasAttemptedProceed] = useState(false);
  const [isLoginInProgress, setIsLoginInProgress] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Music suggestions dialog state
  const [musicDialogEventId, setMusicDialogEventId] = useState<number | undefined>(undefined);
  // Mounted only around its use, but long enough to animate out with the event it showed.
  const isMusicDialogOpen = !!musicDialogEventId;
  const keepMusicDialog = useStaysMountedToClose(isMusicDialogOpen);
  const shownMusicEventId = useLastWhileVisible(isMusicDialogOpen, musicDialogEventId);

  // Handle Google OAuth return flow
  const { isLoadingProfile } = useGoogleAuthReturn();

  // Warm up my-reservations cache so it renders instantly after navigation
  usePrefetchMyReservations();

  // Handle URL parameters for music dialog
  useEffect(() => {
    const musicSuggest = searchParams.get("musicSuggest");
    const eventId = searchParams.get("eventId");

    if (musicSuggest === "true" && eventId) {
      const parsedEventId = Number(eventId);
      if (!isNaN(parsedEventId)) {
        setMusicDialogEventId(parsedEventId);
      }
    }
  }, [searchParams]);
  // Apply theme colors from studio preferences: the studio's Design Studio accent when
  // it has one, else its background colours, as before.
  React.useEffect(() => {
    applyStudioWizardAccent(selectedStudio);
  }, [selectedStudio]);

  const {
    currentStep,
    stepIndex,
    totalSteps,
    selection,
    progressPercentage,
    isTransitioning,
    availableServices,
    serviceCategories,
    servicesByCategory,
    availableEmployees,
    servicesLoading,
    employeesLoading,
    canProceedFromStep,
    isLoading,
    bookingId,
    goToStep,
    nextStep,
    previousStep,
    selectService,
    selectEmployee,
    selectDateTime,
    selectDateOnly,
    jumpToFirstAvailableDay,
    hasAnyAvailableSlots,
    createAppointment,
    triggerAuthRedirect,
  } = useAppointmentWizard();

  const preReservationForm = usePreReservationFormForService(
    selection.service?.id,
  );
  const [showFormDialog, setShowFormDialog] = useState(false);
  const formSubmissionRef = useRef<IPreReservationFormSubmission | null>(null);

  // Re-require the consent form whenever the selected service changes, so a
  // submission filled for one booking is never silently reused for another.
  useEffect(() => {
    formSubmissionRef.current = null;
  }, [selection.service?.id]);

  const { canNavigateBack, canNavigateForward, validationMessage } =
    useWizardValidation(
      currentStep,
      stepIndex,
      totalSteps,
      selection,
      hasAttemptedProceed
    );

  const { isEntering, isExiting } = useWizardAnimations(isTransitioning);

  // Reset validation attempt flag when step changes
  React.useEffect(() => {
    setHasAttemptedProceed(false);
  }, [currentStep]);

  // Smooth scroll to top on step change
  React.useEffect(() => {
    // Find the scrollable container (wizardContent)
    const wizardContent = document.querySelector(`.${styles.wizardContent}`);
    if (wizardContent) {
      wizardContent.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [currentStep]);

  // Handle forward navigation attempt
  const handleNextStep = () => {
    if (!canNavigateForward || !canProceedFromStep) {
      setHasAttemptedProceed(true);
      return;
    }

    // Special handling for confirmation step - create appointment
    if (currentStep === "confirmation") {
      // Check if user is unreliable
      if (currentUser?.isUnreliable) {
        showCustomConfirmDialog(
          <>
            <div>
              За съжаление в момента не можете да направите тази резервация през
              портала.
            </div>
            <br />
            <div>Моля свържете се с обекта за повече информация.</div>
          </>,
          () => callPhoneNumber(selectedStudio?.phoneNumber),
          () => {},
          "warning",
          "Свържи се с обекта",
          "Затвори"
        );
        return;
      }

      // Pre-reservation consent form: block until the user fills it in and agrees.
      if (preReservationForm?.id && !formSubmissionRef.current) {
        setShowFormDialog(true);
        return;
      }

      createAppointment(formSubmissionRef.current);
    } else {
      nextStep();
    }
  };

  const handleFormAgree = (answers: string[]) => {
    if (preReservationForm?.id) {
      formSubmissionRef.current = {
        preReservationFormId: preReservationForm.id,
        answers,
      };
    }
    setShowFormDialog(false);
    handleNextStep();
  };

  const handleFormDisagree = () => {
    setShowFormDialog(false);
    getErrorToast(
      "Резервацията не може да бъде направена без да попълните формуляра.",
    );
  };

  // Show loading screen until studio data is loaded or while processing OAuth
  if (!selectedStudio || isLoadingProfile) {
    return <WizardLoadingScreen />;
  }

  // Render step content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case "start":
        return <StartReservationStep onContinue={nextStep} />;
      case "services":
        return (
          <ServicesSelectionStep
            services={availableServices}
            categories={serviceCategories}
            servicesByCategory={servicesByCategory}
            selectedService={selection.service}
            onServiceSelect={selectService}
            isLoading={isLoading || servicesLoading}
          />
        );
      case "employees":
        return (
          <EmployeesSelectionStep
            employees={availableEmployees}
            selectedEmployee={selection.employee}
            onEmployeeSelect={selectEmployee}
            isLoading={isLoading || employeesLoading}
            serviceId={selection.service?.id}
            studioId={selectedStudio?.id}
          />
        );
      case "datetime":
        return (
          <DateTimeSelectionStep
            selectedService={selection.service}
            selectedEmployee={selection.employee}
            selectedDate={selection.date}
            selectedTime={selection.time}
            onDateSelect={selectDateOnly}
            onTimeSelect={(time) =>
              selectDateTime(selection.date || new Date(), time)
            }
            onJumpToFirstAvailable={jumpToFirstAvailableDay}
            hasAnyAvailableSlots={hasAnyAvailableSlots}
            isLoading={isLoading}
          />
        );
      case "login":
        // Create wizard state for URL encoding
        const wizardStateForLogin = {
          currentStep,
          stepIndex,
          totalSteps,
          isTransitioning,
          canNavigateBack: false,
          canNavigateForward: false,
          selection,
          isLoading,
          error: null,
          bookingId,
        };

        return (
          <LoginStep
            onLoginSuccess={(user) => {
              if (!!user) {
                setIsLoginInProgress(true);
                setIsLoginInProgress(false);
                // Trigger the auth redirect mechanism
                triggerAuthRedirect();
              }
            }}
            wizardState={wizardStateForLogin}
          />
        );
      case "confirmation":
        return (
          <BookingConfirmationStep
            selectedService={selection.service}
            selectedEmployee={selection.employee}
            selectedDate={selection.date}
            selectedTime={selection.time}
            totalPrice={selection.totalPrice}
            isLoading={isLoading}
          />
        );
      case "success":
        return (
          <BookingSuccessStep
            selectedService={selection.service}
            selectedEmployee={selection.employee}
            selectedDate={selection.date}
            selectedTime={selection.time}
            totalPrice={selection.totalPrice}
            bookingId={bookingId || undefined}
            onBookAnother={() => {
              navigate(0);
            }}
            onGoToDashboard={() => {
              navigate(routePaths.portalMyReservations);
            }}
          />
        );
      default:
        return (
          <div className={styles.tempContent}>
            <p>Неизвестна стъпка: {currentStep}</p>
          </div>
        );
    }
  };

  const handleCloseMusicDialog = () => {
    setMusicDialogEventId(undefined);
    // Remove URL params
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("musicSuggest");
    newParams.delete("eventId");
    // replace so closing the music dialog doesn't add a history entry.
    setSearchParams(newParams, { replace: true });
  };

  return (
    <div className={classNames(styles.wizardContainer, className)}>
      {/* Progress Bar */}
      <ProgressBar progress={progressPercentage} />

      {/* Header */}
      <WizardHeader />

      {/* Content Area */}
      <div
        className={classNames(styles.wizardContent, {
          [styles.wizardContentNoScrollbar]: currentStep === "start",
        })}
      >
        <WizardStep
          isActive={true}
          isEntering={isEntering}
          isExiting={isExiting}
        >
          {/* Render step content based on current step */}
          {renderStepContent()}
        </WizardStep>
      </div>

      {/* Navigation */}
      <WizardNavigation
        currentStep={currentStep}
        canNavigateBack={canNavigateBack}
        canNavigateForward={canNavigateForward && canProceedFromStep}
        isLoading={isLoading || isLoginInProgress}
        validationMessage={validationMessage}
        onBack={previousStep}
        onForward={handleNextStep}
      />

      {/* Floating, draggable back control. The wizard drives its steps through
          browser history, so a plain history back steps through the wizard and
          exits it at the first step. */}
      <DraggableBackButton onBack={() => window.history.back()} />

      {/* Music Suggestions Dialog (opened from share link) */}
      {keepMusicDialog && shownMusicEventId && (
        <MusicSuggestionsDialog
          eventId={shownMusicEventId}
          isOpen={isMusicDialogOpen}
          onClose={handleCloseMusicDialog}
          showSearch={false}
        />
      )}

      {preReservationForm?.id && (
        <PreReservationFormDialog
          visible={showFormDialog}
          formName={preReservationForm.name}
          templateText={preReservationForm.templateText}
          contextValues={buildClientFormContext(selectedStudio, currentUser, {
            serviceName: selection.service?.name ?? "",
            eventName: selection.service?.name ?? "",
            // The service's own band, which is what the server resolves for the
            // stored snapshot. Not `totalPrice` (minPrice rounded by the employee's
            // modifier) and not `service.price` (an alias for minPrice) — either
            // would have the user agree to one amount and file another.
            price: formatFormPrice(
              selection.service?.minPrice,
              selection.service?.maxPrice,
            ),
          })}
          onAgree={handleFormAgree}
          onDisagree={handleFormDisagree}
        />
      )}
    </div>
  );
};
